
    import { defineComponent, PropType } from 'vue'

    import { createValidations } from '@/helpers/commons/validationHelper'
    import { AxiosResponse } from 'axios'
    import Dialog from 'primevue/dialog'
    import Textarea from 'primevue/textarea'
    import DataPreparationDescriptor from './DataPreparationDescriptor.json'
    import useValidate from '@vuelidate/core'
    import DataPreparationValidationDescriptor from './DataPreparationValidationDescriptor.json'
    import KnValidationMessages from '@/components/UI/KnValidatonMessages.vue'
    import { IDataPreparationDataset, IDataPreparationColumn } from '@/modules/workspace/dataPreparation/DataPreparation'

    import dataPreparationMonitoringDescriptor from '@/modules/workspace/dataPreparation/DataPreparationMonitoring/DataPreparationMonitoringDescriptor.json'
    import KnScheduler from '@/components/UI/KnScheduler/KnScheduler.vue'

    export default defineComponent({
        name: 'data-preparation-detail-save-dialog',
        props: {
            originalDataset: {} as any,
            config: {} as any,
            columns: [] as PropType<IDataPreparationColumn[]>,
            instanceId: {} as any,
            processId: {} as any,
            preparedDsMeta: {} as any,
            visibility: Boolean
        },
        components: { Dialog, KnScheduler, KnValidationMessages, Textarea },
        data() {
            return {
                descriptor: DataPreparationDescriptor,
                preparedDataset: {} as IDataPreparationDataset,
                v$: useValidate() as any,
                validationDescriptor: DataPreparationValidationDescriptor,
                schedulerDescriptor: dataPreparationMonitoringDescriptor,
                currentCronExpression: '',
                isFirstSave: true,
                touched: false,
                schedulationPaused: false,
                schedulationEnabled: false,
                cronExpressionType: ''
            }
        },
        updated() {
            if (this.processId && this.processId != '') this.isFirstSave = false
        },
        emits: ['update:visibility', 'update:instanceId', 'update:processId'],

        validations() {
            return {
                preparedDataset: createValidations('preparedDataset', this.validationDescriptor.validations.configuration)
            }
        },
        computed: {
            saveButtonDisabled(): any {
                return this.v$.$invalid || !this.preparedDataset.name
            }
        },
        methods: {
            savePreparedDataset(): void {
                let processDefinition = this.createProcessDefinition()
                this.saveOrUpdateProcess(processDefinition).then(
                    (response: AxiosResponse<any>) => {
                        let processId = response.data.id
                        this.$emit('update:processId', processId)
                        let datasetDefinition = this.createDatasetDefinition()
                        this.saveOrUpdateInstance(processId, datasetDefinition).then(
                            (response: AxiosResponse<any>) => {
                                this.$emit('update:instanceId', response.data.id)
                                this.$store.commit('setInfo', { title: 'Saved successfully' })
                            },
                            () => {
                                this.$store.commit('setError', { title: 'Save error', msg: 'Cannot add process instance' })
                            }
                        )
                    },
                    () => {
                        this.$store.commit('setError', { title: 'Save error', msg: 'Cannot create process' })
                    }
                )
                this.resetAndClose()
            },
            saveOrUpdateProcess(processDefinition) {
                if (this.processId && this.processId != '') return this.$http.put(process.env.VUE_APP_DATA_PREPARATION_PATH + `1.0/process/${this.processId}`, processDefinition)
                else return this.$http.post(process.env.VUE_APP_DATA_PREPARATION_PATH + '1.0/process', processDefinition)
            },
            saveOrUpdateInstance(processId, datasetDefinition) {
                if (this.instanceId && this.instanceId != '') return this.$http.post(process.env.VUE_APP_DATA_PREPARATION_PATH + `1.0/instance/${this.instanceId}`, datasetDefinition)
                else return this.$http.post(process.env.VUE_APP_DATA_PREPARATION_PATH + '1.0/process/' + processId + '/instance', datasetDefinition)
            },
            createDatasetDefinition() {
                let toReturn = {}
                toReturn['config'] = {}
                toReturn['config']['paused'] = this.schedulationPaused

                if (this.schedulationEnabled) {
                    toReturn['config']['cron'] = this.currentCronExpression
                    toReturn['config']['type'] = this.cronExpressionType
                }

                toReturn['dataSetLabel'] = this.originalDataset.label
                toReturn['dataSetId'] = this.originalDataset.id
                var d = new Date()
                if (this.preparedDataset.label) {
                    toReturn['destinationDataSetLabel'] = this.preparedDataset.label
                } else {
                    toReturn['destinationDataSetLabel'] = 'ds__' + (d.getTime() % 10000000)
                }
                toReturn['destinationDataSetName'] = this.preparedDataset.name
                toReturn['destinationDataSetDescription'] = this.preparedDataset.description
                toReturn['meta'] = this.createMetaDefinition()
                toReturn['dsId'] = this.preparedDataset.id
                return toReturn
            },
            createMetaDefinition() {
                let meta = [] as Array<any>
                this.columns?.forEach((col) => {
                    let item = {}
                    item['displayedName'] = col.fieldAlias
                    item['name'] = col.header
                    item['fieldType'] = col.fieldType
                    item['type'] = col.Type
                    meta.push(item)
                })
                return meta
            },
            createProcessDefinition() {
                let toReturn = {}
                if (this.config && this.config.transformations) toReturn['definition'] = this.config.transformations
                return toReturn
            },
            cancel() {
                if (this.touched) {
                    this.$confirm.require({
                        message: this.$t('common.toast.unsavedChangesHeader'),
                        header: this.$t('common.toast.unsavedChangesMessage'),
                        icon: 'pi pi-exclamation-triangle',
                        accept: () => this.resetAndClose()
                    })
                } else {
                    this.resetAndClose()
                }
            },
            resetAndClose(): void {
                this.cronExpressionType = this.preparedDsMeta.config?.type
                this.currentCronExpression = this.preparedDsMeta.config?.cron ? this.preparedDsMeta.config.cron : ''

                this.schedulationPaused = this.preparedDsMeta.config?.schedulationPaused || false

                this.schedulationEnabled = this.preparedDsMeta.config?.cron ? true : false
                this.closeDialog()
            },
            closeDialog(): void {
                this.$emit('update:visibility', false)
            },
            loadTranslations(): void {
                this.descriptor.dataPreparation.refreshRate.options.forEach((element) => {
                    element.name = this.$t(element.name)
                })
            },

            updateSchedulationPaused(newSchedulationPaused) {
                this.schedulationPaused = newSchedulationPaused
            },
            updateSchedulationEnabled(newSchedulationEnabled) {
                this.schedulationEnabled = newSchedulationEnabled
            },
            updateCurrentCronExpression(newCronExpression) {
                this.currentCronExpression = newCronExpression
            },
            updateCronExpressionType(newCronExpressionType) {
                this.cronExpressionType = newCronExpressionType
            }
        },

        watch: {
            preparedDsMeta: {
                handler() {
                    if (Object.keys(this.preparedDsMeta).length > 0) {
                        this.preparedDataset = this.preparedDsMeta
                        this.cronExpressionType = this.preparedDsMeta.config?.type
                        this.currentCronExpression = this.preparedDsMeta.config?.cron ? this.preparedDsMeta.config.cron : ''

                        this.schedulationPaused = this.preparedDsMeta.config?.schedulationPaused || false

                        this.schedulationEnabled = this.preparedDsMeta.config?.cron ? true : false
                    }
                },
                deep: true
            }
        },

        created() {
            this.loadTranslations()
        }
    })
